/**
 * iter150AZ.60 — Authenticated file download helper.
 *
 * Background: The CAP frontend authenticates API calls via an axios
 * request interceptor that reads the user's email from ``localStorage``
 * and sets the ``X-User-Email`` header. A plain
 * ``<a href="${BACKEND}/api/some-protected.pdf" target="_blank">`` opens
 * the URL in a NEW TAB — which doesn't go through axios, so the header
 * is never set and the backend returns 403.
 *
 * The first place this bit us was the PM Service Parts Chart PDF
 * (request ``sg_36900e60da5347``). The follow-up sweep refactored
 * EVERY other ``<a href="${BACKEND}/api/...">`` anchor that points at
 * an authenticated endpoint to use this helper instead.
 *
 * Usage:
 *
 *   const { downloading, download } = useAuthenticatedDownload();
 *   …
 *   <button onClick={() => download(`/warranty/master-doc.pdf`,
 *                                   "Warranty_Policy.pdf")}
 *           disabled={downloading}>
 *     ↓ PDF
 *   </button>
 *
 * The helper:
 *   * Streams the file as a Blob via the authenticated ``api`` axios
 *     instance (so X-User-Email + Authorization headers go along).
 *   * Triggers a client-side download via a one-off ``<a download>``
 *     element appended to the body, then removes it.
 *   * Honours the backend's ``Content-Type`` (no hard-coded
 *     application/pdf) so it works for images / signed disclaimers /
 *     attachments alike.
 *   * Surfaces a sonner toast on failure with the real reason —
 *     401/403/404/503 — so users never get a silent blank tab again.
 */
import { useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * @returns {{ downloading: boolean,
 *             download: (path: string, fallbackFilename?: string,
 *                        opts?: { params?: object,
 *                                 successMessage?: string }) => Promise<void> }}
 */
export function useAuthenticatedDownload() {
  const [downloading, setDownloading] = useState(false);

  const download = useCallback(async (path, fallbackFilename = "download", opts = {}) => {
    if (downloading) return;
    setDownloading(true);
    try {
      const r = await api.get(path, {
        responseType: "blob",
        params: opts.params,
      });
      const ct = r.headers?.["content-type"] || "application/octet-stream";
      const blob = new Blob([r.data], { type: ct });
      const url = window.URL.createObjectURL(blob);

      // Prefer the filename the server suggests; fall back to the
      // caller's hint. Format: ``inline; filename="foo.pdf"`` or
      // ``attachment; filename="foo.pdf"`` — both supported.
      let filename = fallbackFilename;
      const cd = r.headers?.["content-disposition"] || "";
      const m = /filename\*?=(?:UTF-\d+''|"|')?([^";]+)/i.exec(cd);
      if (m && m[1]) {
        try { filename = decodeURIComponent(m[1].trim()); } catch { filename = m[1].trim(); }
      }

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      if (opts.successMessage) {
        toast.success(opts.successMessage);
      }
    } catch (e) {
      const status = e?.response?.status;
      let detail = e?.response?.data?.detail || e?.message || "Download failed";
      // Axios returns a Blob for `responseType: "blob"` — even on errors.
      // Try to peek inside if the server sent JSON.
      const blob = e?.response?.data;
      if (blob instanceof Blob && blob.type?.includes("json")) {
        try {
          const text = await blob.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) detail = parsed.detail;
        } catch { /* keep original */ }
      }
      const friendly =
        status === 401 ? "Sign in to download this file." :
        status === 403 ? `Access denied — ${detail}` :
        status === 404 ? "File not found." :
        status === 503 ? "Database unavailable. Please retry in a moment." :
        `Download failed — ${detail}`;
      toast.error(friendly);
    } finally {
      setDownloading(false);
    }
  }, [downloading]);

  return { downloading, download };
}
